import type { CSSProperties } from "react";
import type { AdminFieldDef } from "./adminFieldSchema";
import type { Unit } from "@/api/newBooking";

type Props = {
  field: AdminFieldDef;
  value: unknown;
  previousValue: unknown;
  dirty: boolean;
  onChange: (next: unknown) => void;
  units?: Unit[];
};

const labelStyle: CSSProperties = {
  fontSize: 12,
  fontWeight: 600,
  color: "var(--fg)",
  display: "flex",
  alignItems: "center",
  gap: 6,
};

const hintStyle: CSSProperties = {
  fontSize: 11,
  color: "var(--fg-subtle)",
  marginTop: 2,
};

const previousStyle: CSSProperties = {
  fontSize: 11,
  color: "var(--fg-subtle)",
  marginTop: 4,
  fontStyle: "italic",
};

const inputBase: CSSProperties = {
  width: "100%",
  padding: "6px 10px",
  borderRadius: 6,
  border: "1.5px solid var(--border)",
  background: "var(--bg-input, var(--bg-subtle))",
  color: "var(--fg)",
  fontSize: 13,
  fontFamily: "var(--font-body)",
  boxSizing: "border-box",
};

function asString(v: unknown): string {
  if (v === null || v === undefined) return "";
  return String(v);
}

function formatPrev(v: unknown): string {
  if (v === null || v === undefined || v === "") return "—";
  if (typeof v === "boolean") return v ? "ja" : "nein";
  if (Array.isArray(v)) return v.length ? `[${v.join(", ")}]` : "—";
  return String(v);
}

export function AdminRow({ field, value, previousValue, dirty, onChange, units }: Props) {
  const readOnly = !!field.readOnly;
  const showPrev = dirty && previousValue !== value;

  const input = renderInput(field, value, onChange, readOnly, units);

  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "240px 1fr",
        gap: 16,
        padding: "10px 4px",
        borderBottom: "1px solid var(--border)",
        alignItems: "flex-start",
      }}
    >
      <div>
        <div style={labelStyle}>
          {dirty && (
            <span
              style={{
                width: 6,
                height: 6,
                borderRadius: 3,
                background: "var(--brand-700)",
                display: "inline-block",
              }}
              title="Geändert"
            />
          )}
          <span>{field.label}</span>
        </div>
        <div style={{ fontSize: 10, color: "var(--fg-subtle)", fontFamily: "var(--font-mono)" }}>
          {field.key}
        </div>
        {field.hint && <div style={hintStyle}>{field.hint}</div>}
      </div>
      <div>
        {input}
        {showPrev && <div style={previousStyle}>vorher: {formatPrev(previousValue)}</div>}
      </div>
    </div>
  );
}

function renderInput(
  field: AdminFieldDef,
  value: unknown,
  onChange: (next: unknown) => void,
  readOnly: boolean,
  units?: Unit[],
) {
  const disabledStyle: CSSProperties = readOnly
    ? { background: "var(--bg-subtle)", color: "var(--fg-subtle)", cursor: "not-allowed" }
    : {};
  const style: CSSProperties = { ...inputBase, ...disabledStyle };

  switch (field.type) {
    case "checkbox":
      return (
        <label style={{ display: "inline-flex", alignItems: "center", gap: 8, fontSize: 13, color: "var(--fg)" }}>
          <input
            type="checkbox"
            checked={!!value}
            disabled={readOnly}
            onChange={(e) => onChange(e.target.checked)}
            style={{ width: 16, height: 16 }}
          />
          <span>{value ? "ja" : "nein"}</span>
        </label>
      );
    case "select":
      return (
        <select
          value={asString(value)}
          disabled={readOnly}
          onChange={(e) => onChange(e.target.value || null)}
          style={style}
        >
          <option value="">— bitte wählen —</option>
          {(field.options ?? []).map((opt) => (
            <option key={opt} value={opt}>
              {opt}
            </option>
          ))}
        </select>
      );
    case "textarea":
      return (
        <textarea
          value={asString(value)}
          disabled={readOnly}
          onChange={(e) => onChange(e.target.value)}
          rows={3}
          style={{ ...style, minHeight: 64, resize: "vertical", fontFamily: "var(--font-body)" }}
        />
      );
    case "date":
      return (
        <input
          type="date"
          value={asString(value)}
          disabled={readOnly}
          onChange={(e) => onChange(e.target.value || null)}
          style={style}
        />
      );
    case "time":
      return (
        <input
          type="time"
          value={asString(value)}
          disabled={readOnly}
          onChange={(e) => onChange(e.target.value || null)}
          style={style}
        />
      );
    case "number":
      return (
        <input
          type="number"
          value={asString(value)}
          disabled={readOnly}
          onChange={(e) => onChange(e.target.value === "" ? null : Number(e.target.value))}
          step={1}
          style={style}
        />
      );
    case "decimal":
      return (
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <input
            type="number"
            value={asString(value)}
            disabled={readOnly}
            onChange={(e) => onChange(e.target.value === "" ? null : Number(e.target.value))}
            step="0.01"
            style={style}
          />
          {field.suffix && <span style={{ fontSize: 12, color: "var(--fg-muted)" }}>{field.suffix}</span>}
        </div>
      );
    case "eur":
      return (
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <input
            type="number"
            value={asString(value)}
            disabled={readOnly}
            onChange={(e) => onChange(e.target.value === "" ? null : Number(e.target.value))}
            step="0.01"
            style={style}
          />
          <span style={{ fontSize: 12, color: "var(--fg-muted)" }}>€</span>
        </div>
      );
    case "units":
      return <UnitsInput value={value as number[] | null | undefined} units={units ?? []} disabled={readOnly} onChange={onChange} />;
    case "text":
    default:
      return (
        <input
          type="text"
          value={asString(value)}
          disabled={readOnly}
          onChange={(e) => onChange(e.target.value === "" ? null : e.target.value)}
          style={style}
        />
      );
  }
}

function UnitsInput({
  value,
  units,
  disabled,
  onChange,
}: {
  value: number[] | null | undefined;
  units: Unit[];
  disabled: boolean;
  onChange: (next: number[]) => void;
}) {
  const selected = new Set(value ?? []);
  const toggle = (id: number) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onChange([...next].sort((a, b) => a - b));
  };
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
      {units.length === 0 && (
        <span style={{ fontSize: 12, color: "var(--fg-subtle)" }}>Lade Einheiten…</span>
      )}
      {units.map((u) => {
        const active = selected.has(u.id);
        return (
          <button
            key={u.id}
            type="button"
            disabled={disabled}
            onClick={() => toggle(u.id)}
            style={{
              padding: "5px 10px",
              borderRadius: 6,
              border: active ? "1px solid var(--brand-700)" : "1px solid var(--border)",
              background: active ? "var(--brand-100)" : "var(--bg-subtle)",
              color: active ? "var(--brand-900)" : "var(--fg-muted)",
              fontSize: 12,
              fontWeight: active ? 600 : 500,
              cursor: disabled ? "not-allowed" : "pointer",
              fontFamily: "var(--font-body)",
            }}
          >
            {u.name}
          </button>
        );
      })}
    </div>
  );
}
